import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useCustomizer } from '../../store/customizer';
import { MobileIntro } from '../MobileIntro';
import { MobileChrome } from '../MobileChrome';
import { MorphGhosts } from '../MorphGhosts';
import { resetStepSlideToIntro } from '../MobileStepSlide';
import { measureGhosts, type MorphGhost } from '../lib/morph';
import { Stage, ChromeLayer, IntroLayer } from './MobileApp.styles';

/** 'intro' = the dismiss screen; 'transition' = the morph is playing (both layers
 *  co-mounted); 'chrome' = the per-step wizard. */
type Mode = 'intro' | 'transition' | 'chrome';
/** 'forward' = dismiss (boxes → rail); 'reverse' = back (rail → boxes). */
type Direction = 'forward' | 'reverse';

/**
 * Mobile wizard experience. Shows the intro screen ("Take your website live")
 * before step 1, then the per-step chrome (progress bar · back · globe · sticky
 * CTA) wrapping the active step's mobile body. The intro is mobile-only — the
 * desktop flow never mounts this.
 *
 * Dismissing the intro plays a choreographed FLIP morph: the four stacked stage
 * boxes fly up and flatten into the top progress cluster while the intro plate
 * cross-dissolves to reveal step 1. Going back replays the exact same measured
 * flight in reverse (the rail expands back into the boxes). Both screens are
 * briefly co-mounted so the source + destination rects can be measured and ghost
 * clones flown between them; `prefers-reduced-motion` (and any measurement miss)
 * falls back to an instant swap, byte-equivalent to the old behaviour.
 */
export const MobileApp = (): React.ReactElement => {
  const { displayIntro } = useCustomizer();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const containerRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<Mode>(displayIntro ? 'intro' : 'chrome');
  const [direction, setDirection] = useState<Direction>('forward');
  const [ghosts, setGhosts] = useState<MorphGhost[] | null>(null);
  // The intro plate's cross-dissolve target. Forward: starts shown then dims out;
  // reverse: starts dimmed then fades in. Flipped one rAF after the ghosts paint.
  const [introDimmed, setIntroDimmed] = useState(false);
  const prevIntro = useRef(displayIntro);

  // React to the store's intro flag flipping. Dismiss → forward morph; re-open
  // (step-1 Back) → reverse morph. Motion opt-out skips straight to the end state.
  useEffect(() => {
    const was = prevIntro.current;
    prevIntro.current = displayIntro;
    if (was === displayIntro) return;
    if (!displayIntro && was) {
      if (reducedMotion) {
        setMode('chrome');
        return;
      }
      setDirection('forward');
      setGhosts(null);
      setIntroDimmed(false); // plate starts fully shown, then dims out
      setMode('transition');
    } else if (displayIntro && !was) {
      // Back to the intro: the next dismiss should slide country in forward again.
      resetStepSlideToIntro();
      if (reducedMotion) {
        setMode('intro');
        return;
      }
      setDirection('reverse');
      setGhosts(null);
      setIntroDimmed(true); // plate starts hidden, then fades in
      setMode('transition');
    }
  }, [displayIntro, reducedMotion]);

  // Measure once, the frame the transition mounts: both layers are present and at
  // rest. A measurement miss (no boxes) degrades to an instant swap to the end.
  useLayoutEffect(() => {
    if (mode !== 'transition' || ghosts !== null || !containerRef.current) return;
    const measured = measureGhosts(containerRef.current);
    if (measured) setGhosts(measured);
    else setMode(direction === 'forward' ? 'chrome' : 'intro');
  }, [mode, ghosts, direction]);

  // Flip the plate's cross-dissolve only AFTER the ghosts have painted at rest, so
  // the fade has a baseline to animate from (same-frame flips don't transition).
  useEffect(() => {
    if (mode !== 'transition' || ghosts === null) return undefined;
    const id = requestAnimationFrame(() => setIntroDimmed(direction === 'forward'));
    return () => cancelAnimationFrame(id);
  }, [mode, ghosts, direction]);

  const finishMorph = useCallback(() => {
    setGhosts(null);
    setIntroDimmed(false);
    setMode(direction === 'forward' ? 'chrome' : 'intro');
  }, [direction]);

  if (mode === 'intro') {
    return (
      <Stage ref={containerRef}>
        <MobileIntro />
      </Stage>
    );
  }

  if (mode === 'chrome') {
    // Keep MobileChrome inside ChromeLayer (as during the transition) so its
    // instance — and the mounted step-1 body — persists across the handoff rather
    // than remounting (which would flash the step's own load).
    return (
      <Stage ref={containerRef}>
        <ChromeLayer>
          <MobileChrome>
            <Outlet />
          </MobileChrome>
        </ChromeLayer>
      </Stage>
    );
  }

  // transition: step 1 rests beneath (its rail hidden) while the intro plate
  // cross-dissolves and the ghost clones fly the boxes between rail and stack.
  return (
    <Stage ref={containerRef}>
      <ChromeLayer>
        <MobileChrome entering>
          <Outlet />
        </MobileChrome>
      </ChromeLayer>
      <IntroLayer>
        <MobileIntro dimmed={introDimmed} boxesHidden />
      </IntroLayer>
      {ghosts !== null && <MorphGhosts ghosts={ghosts} reverse={direction === 'reverse'} onDone={finishMorph} />}
    </Stage>
  );
};

export default MobileApp;
